import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableFooter,
  TableRow,
  CircularProgress,
  IconButton,
  Backdrop,
  Divider,
  Button,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Close, DragIndicator } from '@mui/icons-material';
import { format } from 'date-fns';
import type { EstimateHeader } from '../../services/estimateService';
import type { PozycjeKosztorysStats } from '../../utils/sumNetFromEasyPrzedmiarRows';

export type ColKey =
  | 'id'
  | 'pozycje'
  | 'date'
  | 'client'
  | 'object_name'
  | 'branza'
  | 'net'
  | 'vat_col'
  | 'brutto'
  | 'status'
  | 'actions';

const ALL_COLUMNS: ColKey[] = [
  'id',
  'pozycje',
  'date',
  'client',
  'object_name',
  'branza',
  'net',
  'vat_col',
  'brutto',
  'status',
  'actions',
];

const COL_LABELS: Record<ColKey, string> = {
  id: 'Id',
  pozycje: 'Pozycje',
  date: 'Data',
  client: 'Zamawiający',
  object_name: 'Nazwa obiektu',
  branza: 'Branża',
  net: 'Netto',
  vat_col: 'VAT',
  brutto: 'Brutto',
  status: 'Status',
  actions: 'Akcje',
};

const DEFAULT_WIDTHS: Record<ColKey, number> = {
  id: 120,
  pozycje: 102,
  date: 110,
  client: 200,
  object_name: 200,
  branza: 140,
  net: 110,
  vat_col: 100,
  brutto: 110,
  status: 100,
  actions: 48,
};

const STORAGE_KEY_WIDTHS = 'easyEstimateOfferKosztorysyColWidths';
const STORAGE_PREFIX = 'easyEstimateOfferKosztorysyTableCol';
const VISIBLE_KEY = `${STORAGE_PREFIX}:visible`;
const ORDER_KEY = `${STORAGE_PREFIX}:order`;

const COLUMNS_DIALOG = { width: 305, height: 460 } as const;

/** Zapisana kolejność z localStorage + nowe kolumny; „Pozycje” tuż za „Id” (migracja ze starszych wersji). */
function mergeSavedColumnOrder(saved: string[]): ColKey[] {
  const filtered = saved.filter((k): k is ColKey => ALL_COLUMNS.includes(k as ColKey));
  let merged: ColKey[] = filtered.concat(ALL_COLUMNS.filter((k) => !filtered.includes(k)));
  if (!filtered.includes('pozycje')) {
    merged = merged.filter((k) => k !== 'pozycje');
    const idIdx = merged.indexOf('id');
    if (idIdx >= 0) merged.splice(idIdx + 1, 0, 'pozycje');
    else merged.unshift('pozycje');
    for (const k of ALL_COLUMNS) {
      if (!merged.includes(k)) merged.push(k);
    }
  }
  return merged;
}

function formatMoney(n: number | null | undefined) {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return new Intl.NumberFormat('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

function statusPl(s: string) {
  switch (s) {
    case 'draft':
      return 'Roboczy';
    case 'finished':
      return 'Zakończony';
    default:
      return s;
  }
}

export type TotalsRow = { net: number; vat: number; brutto: number };

type Props = {
  headers: EstimateHeader[];
  totalsById: Record<number, TotalsRow>;
  /** Z ceną jedn. / łącznie pozycji (przedmiar EasyEstimate albo pozycje KNR z API). */
  pozycjeStatsById: Record<number, PozycjeKosztorysStats>;
  loading: boolean;
  onRowClick: (id: number) => void;
  onMenuButtonClick: (e: React.MouseEvent<HTMLElement>, id: number) => void;
};

const EasyEstimateOfferKosztorysyTable: React.FC<Props> = ({
  headers,
  totalsById,
  pozycjeStatsById,
  loading,
  onRowClick,
  onMenuButtonClick,
}) => {
  const [colResizeMode, setColResizeMode] = React.useState(false);
  const [dragCol, setDragCol] = React.useState<{
    key: ColKey;
    startX: number;
    startW: number;
    rightKey?: ColKey;
    startWRight?: number;
  } | null>(null);

  const [columnWidths, setColumnWidths] = React.useState<Record<ColKey, number>>(DEFAULT_WIDTHS);
  const getWidthNum = React.useCallback(
    (key: ColKey) => columnWidths[key] ?? DEFAULT_WIDTHS[key] ?? 40,
    [columnWidths]
  );
  const getColW = React.useCallback((key: ColKey) => `${getWidthNum(key)}px`, [getWidthNum]);

  const [columnOrder, setColumnOrder] = React.useState<ColKey[]>(() => {
    try {
      const raw = localStorage.getItem(ORDER_KEY);
      if (raw) {
        const ord = JSON.parse(raw) as string[];
        if (Array.isArray(ord) && ord.length) {
          return mergeSavedColumnOrder(ord);
        }
      }
    } catch {}
    return [...ALL_COLUMNS];
  });

  const [columnsVisible, setColumnsVisible] = React.useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem(VISIBLE_KEY);
      if (raw) {
        const obj = JSON.parse(raw);
        if (obj && typeof obj === 'object') return obj;
      }
    } catch {}
    return {};
  });

  const colV = React.useCallback((key: ColKey) => columnsVisible[key] !== false, [columnsVisible]);

  const [colsOpen, setColsOpen] = React.useState(false);
  const [draftColumnsVisible, setDraftColumnsVisible] = React.useState<Record<string, boolean> | null>(null);
  const [draftOrder, setDraftOrder] = React.useState<ColKey[] | null>(null);
  const [draftWidths, setDraftWidths] = React.useState<Partial<Record<ColKey, number>>>({});
  const [colsPosition, setColsPosition] = React.useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [colsDragging, setColsDragging] = React.useState<{ startX: number; startY: number; top: number; left: number } | null>(null);

  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_WIDTHS);
      if (raw) {
        const parsed = JSON.parse(raw) as Record<string, number>;
        if (parsed && typeof parsed === 'object') {
          setColumnWidths((prev) => ({ ...DEFAULT_WIDTHS, ...prev, ...parsed }));
        }
      }
    } catch {}
  }, []);

  React.useEffect(() => {
    try {
      localStorage.setItem(ORDER_KEY, JSON.stringify(columnOrder));
    } catch {}
  }, [columnOrder]);

  React.useEffect(() => {
    try {
      localStorage.setItem(VISIBLE_KEY, JSON.stringify(columnsVisible));
    } catch {}
  }, [columnsVisible]);

  React.useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_WIDTHS, JSON.stringify(columnWidths));
    } catch {}
  }, [columnWidths]);

  React.useEffect(() => {
    if (!dragCol) return;
    const onMove = (e: MouseEvent) => {
      if (!dragCol) return;
      const dx = e.clientX - dragCol.startX;
      const minW = 24;
      const newW = Math.max(minW, dragCol.startW + dx);
      if (dragCol.rightKey && typeof dragCol.startWRight === 'number') {
        const newRight = Math.max(minW, dragCol.startWRight - dx);
        setColumnWidths((prev) => ({ ...prev, [dragCol.key]: newW, [dragCol.rightKey!]: newRight }));
        setDraftWidths((prev) => ({ ...prev, [dragCol.key]: newW, [dragCol.rightKey!]: newRight }));
      } else {
        setColumnWidths((prev) => ({ ...prev, [dragCol.key]: newW }));
        setDraftWidths((prev) => ({ ...prev, [dragCol.key]: newW }));
      }
    };
    const onUp = () => setDragCol(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp, { once: true } as { once: boolean });
    return () => {
      window.removeEventListener('mousemove', onMove);
    };
  }, [dragCol]);

  React.useEffect(() => {
    if (!colsDragging) return;
    const onMove = (e: MouseEvent) => {
      const dx = e.clientX - colsDragging.startX;
      const dy = e.clientY - colsDragging.startY;
      const nextTop = Math.max(0, Math.min((window?.innerHeight || 0) - COLUMNS_DIALOG.height, colsDragging.top + dy));
      const nextLeft = Math.max(0, Math.min(window?.innerWidth || 0, colsDragging.left + dx));
      setColsPosition({ top: nextTop, left: nextLeft });
    };
    const onUp = () => setColsDragging(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [colsDragging]);

  React.useEffect(() => {
    if (!colsOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.keyCode === 27) {
        setColsOpen(false);
        setColResizeMode(false);
        setDraftColumnsVisible(null);
        setDraftOrder(null);
        setDraftWidths({});
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [colsOpen]);

  const openColumnsPanel = () => {
    setDraftColumnsVisible({ ...columnsVisible });
    setDraftOrder([...columnOrder]);
    setDraftWidths({ ...columnWidths });
    setColResizeMode(true);
    setColsOpen(true);
    setColsPosition({ top: 0, left: 0 });
  };

  const visibleCount = React.useMemo(
    () => (draftOrder ?? columnOrder).filter((k) => colV(k)).length,
    [draftOrder, columnOrder, colV]
  );

  const footerSums = React.useMemo(() => {
    let net = 0;
    let vat = 0;
    let brutto = 0;
    let pozycjeWithCena = 0;
    let pozycjeTotal = 0;
    for (const h of headers) {
      const t = totalsById[h.id];
      if (t) {
        net += t.net;
        vat += t.vat;
        brutto += t.brutto;
      }
      const ps = pozycjeStatsById[h.id];
      if (ps && typeof ps.total === 'number' && !Number.isNaN(ps.total)) {
        pozycjeTotal += ps.total;
        pozycjeWithCena += typeof ps.withCenaJedn === 'number' && !Number.isNaN(ps.withCenaJedn) ? ps.withCenaJedn : 0;
      }
    }
    return {
      net: Math.round(net * 100) / 100,
      vat: Math.round(vat * 100) / 100,
      brutto: Math.round(brutto * 100) / 100,
      pozycjeWithCena,
      pozycjeTotal,
    };
  }, [headers, totalsById, pozycjeStatsById]);

  const showFooter = !loading && headers.length > 0;

  const renderResizableTh = (keyId: ColKey, node: React.ReactNode, align?: 'left' | 'right' | 'center') => {
    const ord = (draftOrder ?? columnOrder).filter((k) => colV(k));
    const idx = ord.indexOf(keyId);
    const rightKey = idx >= 0 && idx < ord.length - 1 ? ord[idx + 1] : undefined;

    return (
      <TableCell
        align={align}
        sx={{
          padding: '1px 4px',
          width: getColW(keyId),
          maxWidth: 'none',
          position: 'relative',
          userSelect: 'none',
          backgroundColor: '#FAFAFA',
          borderRight: keyId === 'actions' ? 'none' : '1px solid #d4d4d9',
          fontFamily: 'Roboto, sans-serif',
          fontWeight: 400,
          fontSize: '14px',
          color: '#2D2D32',
          verticalAlign: 'bottom',
        }}
      >
        {node}
        {colResizeMode ? (
          <Box
            role="separator"
            aria-orientation="vertical"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDragCol({
                key: keyId,
                startX: e.clientX,
                startW: getWidthNum(keyId),
                rightKey,
                startWRight: rightKey ? getWidthNum(rightKey) : undefined,
              });
            }}
            sx={{
              position: 'absolute',
              right: 0,
              top: 0,
              width: '12px',
              height: '100%',
              cursor: 'col-resize',
              zIndex: 6,
              touchAction: 'none',
              backgroundImage: 'linear-gradient(to right, transparent 45%, #FF0000 45%, #FF0000 55%, transparent 55%)',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' },
            }}
          />
        ) : null}
      </TableCell>
    );
  };

  const renderHeaderLabel = (key: ColKey) => COL_LABELS[key];

  const renderBodyCell = (key: ColKey, h: EstimateHeader, dateStr: string, idLabel: string) => {
    const t = totalsById[h.id];
    const baseNum = { fontVariantNumeric: 'tabular-nums' as const, fontSize: '0.86rem', color: '#2D2D32' };
    switch (key) {
      case 'id':
        return <TableCell sx={{ ...baseNum, width: getColW('id') }}>{idLabel}</TableCell>;
      case 'pozycje': {
        const s = pozycjeStatsById[h.id];
        const text =
          s && typeof s.total === 'number' && !Number.isNaN(s.total)
            ? `${typeof s.withCenaJedn === 'number' && !Number.isNaN(s.withCenaJedn) ? s.withCenaJedn : 0}/${s.total}`
            : '—';
        return (
          <TableCell align="right" sx={{ ...baseNum, width: getColW('pozycje') }}>
            {text}
          </TableCell>
        );
      }
      case 'date':
        return <TableCell sx={{ ...baseNum, width: getColW('date') }}>{dateStr}</TableCell>;
      case 'client':
        return (
          <TableCell
            sx={{
              width: getColW('client'),
              maxWidth: getColW('client'),
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: '0.86rem',
              color: '#2D2D32',
            }}
          >
            {h.client}
          </TableCell>
        );
      case 'object_name':
        return (
          <TableCell
            sx={{
              width: getColW('object_name'),
              maxWidth: getColW('object_name'),
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: '0.86rem',
              color: '#2D2D32',
            }}
          >
            {h.object_name}
          </TableCell>
        );
      case 'branza':
        return (
          <TableCell
            sx={{
              width: getColW('branza'),
              maxWidth: getColW('branza'),
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: '0.86rem',
              color: '#2D2D32',
            }}
            title={h.branza || undefined}
          >
            {h.branza?.trim() ? h.branza : '—'}
          </TableCell>
        );
      case 'net':
        return (
          <TableCell align="right" sx={{ ...baseNum, width: getColW('net') }}>
            {formatMoney(t?.net)}
          </TableCell>
        );
      case 'vat_col':
        return (
          <TableCell align="right" sx={{ ...baseNum, width: getColW('vat_col') }}>
            {formatMoney(t?.vat)}
          </TableCell>
        );
      case 'brutto':
        return (
          <TableCell align="right" sx={{ ...baseNum, width: getColW('brutto') }}>
            {formatMoney(t?.brutto)}
          </TableCell>
        );
      case 'status':
        return (
          <TableCell sx={{ fontSize: '0.86rem', color: '#2D2D32', width: getColW('status') }}>{statusPl(h.status)}</TableCell>
        );
      case 'actions':
        return (
          <TableCell
            sx={{ width: getColW('actions'), py: 0 }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <IconButton size="small" aria-label="Więcej" onClick={(e) => onMenuButtonClick(e, h.id)}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </TableCell>
        );
      default:
        return null;
    }
  };

  const ordForColgroup = draftOrder ?? columnOrder;

  return (
    <Box sx={{ position: 'relative', width: '100%', pt: '40px' }}>
      <Box sx={{ position: 'absolute', top: 0, right: 0, display: 'flex', alignItems: 'center', zIndex: 3 }}>
        <IconButton
          size="small"
          title="Dostosuj kolumny"
          aria-label="Dostosuj kolumny"
          sx={{
            width: 32,
            height: 32,
            borderRadius: '4px',
            p: 0,
            alignSelf: 'flex-end',
            bgcolor: colResizeMode ? '#E0E0F5' : 'transparent',
            transition: 'background-color 0.15s ease',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' },
          }}
          onClick={openColumnsPanel}
        >
          <Box component="img" alt="kolumny" src="/assets/icons/frame-344.svg?v=2" sx={{ width: 32, height: 32 }} />
        </IconButton>
      </Box>

      <Box sx={{ position: 'relative' }}>
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 3,
              bgcolor: 'rgba(255,255,255,0.5)',
            }}
          >
            <CircularProgress size={36} />
          </Box>
        )}
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: 'min(70vh, 720px)',
            overflow: 'auto',
            border: '1px solid #d0d0d6',
            borderRadius: 1,
            boxShadow: 'none',
          }}
        >
          <Table
            size="small"
            stickyHeader
            sx={{
              tableLayout: 'fixed',
              width: '100%',
              '& .MuiTableCell-root': { fontFamily: 'Roboto, sans-serif' },
            }}
          >
            <colgroup>
              {ordForColgroup.filter((k) => colV(k)).map((key) => (
                <col key={`col-${key}`} style={{ width: getColW(key) }} />
              ))}
            </colgroup>
            <TableHead>
              <TableRow>
                {(draftOrder ?? columnOrder)
                  .filter((k) => colV(k))
                  .map((key) =>
                    renderResizableTh(
                      key,
                      renderHeaderLabel(key),
                      key === 'pozycje' || key === 'net' || key === 'vat_col' || key === 'brutto' ? 'right' : undefined
                    )
                  )}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && headers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={Math.max(visibleCount, 1)} sx={{ py: 4, textAlign: 'center', color: '#5F6368', fontSize: '14px' }}>
                    Brak kosztorysów przypisanych do tej oferty.
                  </TableCell>
                </TableRow>
              )}
              {headers.map((h) => {
                const dateStr = h.prepared_at
                  ? (() => {
                      try {
                        return format(new Date(h.prepared_at), 'yyyy-MM-dd');
                      } catch {
                        return String(h.prepared_at);
                      }
                    })()
                  : '—';
                const idLabel = h.estimate_number || String(h.id);
                return (
                  <TableRow
                    hover
                    key={h.id}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => onRowClick(h.id)}
                  >
                    {(draftOrder ?? columnOrder)
                      .filter((k) => colV(k))
                      .map((key) => (
                        <React.Fragment key={`${h.id}-${String(key)}`}>{renderBodyCell(key, h, dateStr, idLabel)}</React.Fragment>
                      ))}
                  </TableRow>
                );
              })}
            </TableBody>
            {showFooter && (
              <TableFooter>
                <TableRow
                  sx={{
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 2,
                    backgroundColor: '#ececf2',
                    '& .MuiTableCell-root': {
                      borderTop: '2px solid #d0d0d6',
                      fontWeight: 600,
                      fontSize: '0.86rem',
                      color: '#202124',
                      fontVariantNumeric: 'tabular-nums',
                    },
                  }}
                >
                  {(draftOrder ?? columnOrder)
                    .filter((k) => colV(k))
                    .map((key, idx) => {
                      const w = getColW(key);
                      if (key === 'pozycje') {
                        return (
                          <TableCell key="foot-pozycje" align="right" sx={{ width: w }}>
                            {footerSums.pozycjeTotal > 0
                              ? `${footerSums.pozycjeWithCena}/${footerSums.pozycjeTotal}`
                              : '—'}
                          </TableCell>
                        );
                      }
                      if (key === 'net') {
                        return (
                          <TableCell key="foot-net" align="right" sx={{ width: w }}>
                            {formatMoney(footerSums.net)}
                          </TableCell>
                        );
                      }
                      if (key === 'vat_col') {
                        return (
                          <TableCell key="foot-vat" align="right" sx={{ width: w }}>
                            {formatMoney(footerSums.vat)}
                          </TableCell>
                        );
                      }
                      if (key === 'brutto') {
                        return (
                          <TableCell key="foot-brutto" align="right" sx={{ width: w }}>
                            {formatMoney(footerSums.brutto)}
                          </TableCell>
                        );
                      }
                      if (idx === 0) {
                        return (
                          <TableCell key="foot-label" sx={{ width: w }}>
                            Razem
                          </TableCell>
                        );
                      }
                      return (
                        <TableCell
                          key={`foot-${key}`}
                          sx={{ width: w, borderRight: key === 'actions' ? 'none' : undefined }}
                        />
                      );
                    })}
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Box>

      <Backdrop
        open={colsOpen}
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.modal + 1,
          backgroundColor: 'rgba(0,0,0,0.35)',
          pointerEvents: 'none',
        }}
        onClick={() => {
          setColsOpen(false);
          setDraftColumnsVisible(null);
          setDraftOrder(null);
          setDraftWidths({});
        }}
      >
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 0,
            right: 0,
            height: `${COLUMNS_DIALOG.height}px`,
            width: `${COLUMNS_DIALOG.width}px`,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0px 5px 30px rgba(0,0,0,0.15)',
            borderRadius: 0,
            zIndex: (theme) => theme.zIndex.modal + 2,
            backgroundColor: '#fff',
            top: colsPosition.top ? `${colsPosition.top}px` : undefined,
            left: colsPosition.left ? `${colsPosition.left}px` : undefined,
            pointerEvents: 'auto',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Box
            sx={{
              px: 2,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flex: '0 0 auto',
              position: 'sticky',
              top: 0,
              bgcolor: '#FFFFFF',
              zIndex: 1,
              cursor: 'move',
              userSelect: 'none',
            }}
            onMouseDown={(e) => setColsDragging({ startX: e.clientX, startY: e.clientY, top: colsPosition.top, left: colsPosition.left })}
          >
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 16, color: '#2D2D32' }}>
              Dostosuj kolumny
            </Typography>
            <IconButton
              size="small"
              onClick={() => {
                setColsOpen(false);
                setColResizeMode(false);
                setDraftColumnsVisible(null);
                setDraftOrder(null);
                setDraftWidths({});
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ px: 0, py: 1.5, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
            {(draftOrder ?? columnOrder).map((key) => (
              <Box
                key={key}
                sx={{ py: 1, px: 2 }}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('text/plain', key);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const src = e.dataTransfer.getData('text/plain') as ColKey;
                  if (!src || src === key) return;
                  setDraftOrder((prev) => {
                    const cur = [...(prev ?? columnOrder)];
                    const from = cur.indexOf(src);
                    const to = cur.indexOf(key);
                    if (from < 0 || to < 0) return cur;
                    const [removed] = cur.splice(from, 1);
                    cur.splice(to, 0, removed);
                    return cur;
                  });
                }}
              >
                <Box
                  sx={{
                    border: '1px solid #d4d4d9',
                    borderRadius: '4px',
                    height: 36,
                    width: 257,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 1.25,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <IconButton
                      onClick={() =>
                        setDraftColumnsVisible((prev) => {
                          const merged = { ...columnsVisible, ...prev };
                          const isVisible = merged[key] !== false;
                          return { ...merged, [key]: isVisible ? false : true };
                        })
                      }
                      size="small"
                      sx={{ p: 0 }}
                      aria-label="Przełącz widoczność kolumny"
                    >
                      <Box
                        component="img"
                        src={
                          { ...columnsVisible, ...(draftColumnsVisible || {}) }[key] !== false
                            ? '/assets/icons/checkbox-on.svg'
                            : '/assets/icons/checkbox-off.svg'
                        }
                        alt="check"
                        sx={{ width: 23, height: 24 }}
                      />
                    </IconButton>
                    <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#2D2D32' }}>
                      {COL_LABELS[key]}
                    </Typography>
                  </Box>
                  <DragIndicator fontSize="small" sx={{ color: '#595966', cursor: 'grab' }} />
                </Box>
              </Box>
            ))}
          </Box>
          <Divider />
          <Box
            sx={{
              px: 2,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '24px',
              flex: '0 0 auto',
              boxShadow: '0px 4px 4px rgba(0,0,0,0.05)',
              position: 'sticky',
              bottom: 0,
              bgcolor: '#FFFFFF',
              zIndex: 1,
            }}
          >
            <Button
              variant="text"
              onClick={() => {
                setColsOpen(false);
                setColResizeMode(false);
                setDraftColumnsVisible(null);
                setDraftOrder(null);
                setDraftWidths({});
              }}
              sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#2D2D32', textTransform: 'none' }}
            >
              Anuluj
            </Button>
            <Button
              variant="contained"
              sx={{
                width: 123,
                height: 40,
                borderRadius: '4px',
                textTransform: 'none',
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 600,
                fontSize: 14,
                backgroundColor: '#020242',
                '&:hover': { backgroundColor: '#020242' },
              }}
              onClick={() => {
                if (draftColumnsVisible) {
                  setColumnsVisible(draftColumnsVisible);
                  try {
                    localStorage.setItem(VISIBLE_KEY, JSON.stringify(draftColumnsVisible));
                  } catch {}
                }
                if (draftOrder) {
                  const normalized = draftOrder
                    .filter((k) => ALL_COLUMNS.includes(k))
                    .concat(ALL_COLUMNS.filter((k) => !draftOrder.includes(k)));
                  setColumnOrder(normalized);
                  try {
                    localStorage.setItem(ORDER_KEY, JSON.stringify(normalized));
                  } catch {}
                }
                const widthsToSave = { ...columnWidths };
                const finalOrder = draftOrder ?? columnOrder;
                finalOrder.forEach((key) => {
                  const draftW = draftWidths[key];
                  if (draftW && typeof draftW === 'number' && draftW >= 24) {
                    widthsToSave[key] = draftW;
                  }
                });
                setColumnWidths(widthsToSave);
                try {
                  localStorage.setItem(STORAGE_KEY_WIDTHS, JSON.stringify(widthsToSave));
                } catch {}
                setColsOpen(false);
                setColResizeMode(false);
                setDraftColumnsVisible(null);
                setDraftOrder(null);
                setDraftWidths({});
              }}
            >
              Zapisz zmiany
            </Button>
          </Box>
        </Paper>
      </Backdrop>
    </Box>
  );
};

export default EasyEstimateOfferKosztorysyTable;
